/**
 * src/marketing/legalPage.tsx - presentation for the getabeam.com Legal Center (web-only).
 *
 * Two exports:
 *  - <LegalCenter>  the hub at /legal: lists every policy in src/marketing/legal.ts.
 *  - <LegalPage doc=...>  renders a single policy (Terms, Privacy, Refunds).
 *
 * Both wrap MarketingShell so they inherit the site nav + footer. All color/type come from
 * theme tokens; the few layout constants (reading column width) live here with the marketing
 * code. Content comes entirely from legal.ts so wording stays in one place.
 *
 * NEVER imports @smartbox/* (mobile invariant 1).
 */
import React from 'react';
import { Pressable, StyleSheet, View, type DimensionValue } from 'react-native';
import { Text, Card } from '../components/ui';
import { theme } from '../theme';
import { MarketingShell, useMarketingNav } from './shell';
import { Section, Eyebrow } from './sections';
import { openExternalUrl } from '../platform/linking';
import { LEGAL_DOCS, LEGAL_META, type LegalDoc } from './legal';

const READ_W = 760; // readable measure for long-form legal copy

// --- Legal Center hub ----------------------------------------------------------------------

function LegalCenterBody() {
  const { navigate } = useMarketingNav();
  return (
    <Section tone="paper">
      <Eyebrow>Legal</Eyebrow>
      <Text variant="display">Legal Center</Text>
      <Text variant="subhead" color="secondary" style={styles.lede}>
        The policies that govern your use of {LEGAL_META.product}. Last updated {LEGAL_META.updated}.
      </Text>
      <View style={styles.cards}>
        {LEGAL_DOCS.map((doc) => (
          <Pressable
            key={doc.slug}
            accessibilityRole="link"
            onPress={() => navigate(`/legal/${doc.slug}`)}
            style={styles.cardWrap}
          >
            <Card style={styles.card}>
              <Text variant="subhead">{doc.title}</Text>
              <Text variant="body" color="secondary" style={styles.cardSummary}>
                {doc.summary}
              </Text>
              <Text variant="bodyStrong" color="link">
                Read
              </Text>
            </Card>
          </Pressable>
        ))}
      </View>
      <Text variant="small" color="secondary" style={styles.contact}>
        Questions? Contact {LEGAL_META.entity} through{' '}
        <Text variant="small" color="link" onPress={() => void openExternalUrl(LEGAL_META.contactUrl)}>
          {LEGAL_META.contactDisplay}
        </Text>
        .
      </Text>
    </Section>
  );
}

export function LegalCenter() {
  return (
    <MarketingShell>
      <LegalCenterBody />
    </MarketingShell>
  );
}

// --- Single policy -------------------------------------------------------------------------

function PolicyBody({ doc }: { doc: LegalDoc }) {
  const { navigate } = useMarketingNav();
  return (
    <Section tone="paper">
      <View style={styles.doc}>
        <Pressable accessibilityRole="link" onPress={() => navigate('/legal')} style={styles.back}>
          <Text variant="small" color="link">
            ← Legal Center
          </Text>
        </Pressable>

        <Eyebrow>Legal</Eyebrow>
        <Text variant="display">{doc.title}</Text>
        <Text variant="small" color="secondary" style={styles.updated}>
          Last updated {LEGAL_META.updated}
        </Text>

        {doc.intro.map((p, i) => (
          <Text key={`intro-${i}`} variant="body" color="secondary" style={styles.para}>
            {p}
          </Text>
        ))}

        {doc.sections.map((s) => (
          <View key={s.heading} style={styles.section}>
            <Text variant="subhead">{s.heading}</Text>
            {s.body?.map((p, i) => (
              <Text key={`b-${i}`} variant="body" color="secondary" style={styles.para}>
                {p}
              </Text>
            ))}
            {s.bullets ? (
              <View style={styles.bullets}>
                {s.bullets.map((b, i) => (
                  <View key={`li-${i}`} style={styles.bulletRow}>
                    <Text variant="body" color="secondary" style={styles.bulletDot}>
                      •
                    </Text>
                    <Text variant="body" color="secondary" style={styles.flex1}>
                      {b}
                    </Text>
                  </View>
                ))}
              </View>
            ) : null}
          </View>
        ))}
      </View>
    </Section>
  );
}

export function LegalPage({ doc }: { doc: LegalDoc }) {
  return (
    <MarketingShell>
      <PolicyBody doc={doc} />
    </MarketingShell>
  );
}

const styles = StyleSheet.create({
  lede: { marginTop: theme.spacing.sm, maxWidth: READ_W, marginBottom: theme.spacing.xl },
  cards: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -theme.spacing.sm },
  cardWrap: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.sm,
    flexBasis: '100%' as DimensionValue,
    maxWidth: 380 as DimensionValue,
    flexGrow: 1,
  },
  card: { height: '100%', gap: theme.spacing.sm, minHeight: 180 },
  cardSummary: { flex: 1, lineHeight: 22 },
  contact: { marginTop: theme.spacing['2xl'], maxWidth: READ_W },

  doc: { maxWidth: READ_W },
  back: { marginBottom: theme.spacing.lg, alignSelf: 'flex-start' },
  updated: { marginTop: theme.spacing.sm, marginBottom: theme.spacing.lg },
  para: { marginTop: theme.spacing.md, lineHeight: 24 },
  section: { marginTop: theme.spacing['2xl'] },
  bullets: { marginTop: theme.spacing.sm, gap: theme.spacing.sm },
  bulletRow: { flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing.sm },
  bulletDot: { lineHeight: 24 },
  flex1: { flex: 1, lineHeight: 24 },
});
